import React, { useEffect, useMemo } from 'react';
import { Col, Form, Input, InputNumber, Row, Select, Spin, Switch } from 'antd';
import { useInfiniteProducts } from '@/store/Product/useInfiniteProducts';
import { Search } from 'lucide-react';
import {
    currencyFormatterToBRL,
    parseBRLToCents,
} from '@/helpers/currencyParser';
import { handleInfiniteScroll, hasOnlyNumber } from './utils';

import * as S from './styles';
import { ProductCard } from '../ProductCard';

const { Option } = Select;

export const FormPlanogram = () => {
    const form = Form.useFormInstance();

    const searchedProductName: string = Form.useWatch('searchText', form);

    const productId = Form.useWatch(['planogram', 'product_id'], form);

    const isEditing = Form.useWatch('isEditing', form);

    const {
        data: productsPaginated,
        hasNextPage: hasNextPageProducts,
        fetchNextPage: fetchNextPageProducts,
        isFetching: isFetchingProducts,
        isLoading: isLoadingProducts,
    } = useInfiniteProducts({
        name: hasOnlyNumber(searchedProductName)
            ? undefined
            : searchedProductName,
        perPage: 10,
        barCode: hasOnlyNumber(searchedProductName)
            ? searchedProductName
            : undefined,
    });

    const statusOptions = [
        { label: 'Desbloqueado', value: 'unlocked' },
        { label: 'Bloqueado para abastecimento', value: 'lock' },
        { label: 'Bloqueado para venda', value: 'lockSold' },
    ];

    const productOptions = useMemo(() => {
        return productsPaginated?.pages.flatMap((page) => page.data) ?? [];
    }, [productsPaginated]);

    const productSelected = productOptions.filter(
        (product) => product.id === productId,
    )[0];

    useEffect(() => {
        form.setFieldValue(['planogram', 'name'], productSelected?.name);
        form.setFieldValue(
            ['planogram', 'bar_code'],
            productSelected?.BarCodes?.[0]?.code,
        );
    }, [productSelected, form]);

    return (
        <S.Container>
            {isLoadingProducts && isEditing && (
                <S.LoadingContainer>
                    <Spin size="large" />
                </S.LoadingContainer>
            )}
            <Row gutter={16}>
                <Col span={24}>
                    <S.FormItemWrapper hidden={!!productSelected}>
                        <S.FormItemLabel required>
                            Selecione o produto na lista de produtos
                        </S.FormItemLabel>
                        <S.SearchWrapper>
                            <S.SearchIcon>
                                <Search
                                    size={14}
                                    strokeWidth={3}
                                    color="#31374a"
                                />
                            </S.SearchIcon>

                            <Form.Item
                                name="isEditing"
                                noStyle
                                hidden
                                initialValue={false}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>

                            <Form.Item
                                name={['planogram', 'name']}
                                noStyle
                                hidden
                            >
                                <Input value={productSelected?.name || ''} />
                            </Form.Item>

                            <Form.Item
                                name={['planogram', 'bar_code']}
                                noStyle
                                hidden
                            >
                                <Input
                                    value={
                                        productSelected?.BarCodes?.[0]?.code ||
                                        ''
                                    }
                                />
                            </Form.Item>
                            <Form.Item
                                name={['planogram', 'product_id']}
                                label="Selecione o produto na lista de produtos"
                                noStyle
                                hidden={!!productSelected}
                                rules={[
                                    {
                                        required: true,
                                        message:
                                            'Selecione no mínimo um produto',
                                    },
                                ]}
                            >
                                <S.SearchSelect
                                    placeholder="Procure um produto pelo nome ou código de barras"
                                    showSearch
                                    suffixIcon={null}
                                    allowClear
                                    onBlur={() =>
                                        form.setFieldValue('searchText', '')
                                    }
                                    maxTagCount="responsive"
                                    filterOption={false}
                                    onSearch={(v) => {
                                        form.setFieldValue('searchText', v);
                                    }}
                                    onPopupScroll={(e) =>
                                        handleInfiniteScroll({
                                            e,
                                            hasNextPage: hasNextPageProducts,
                                            isFetching: isFetchingProducts,
                                            fetchNextPage:
                                                fetchNextPageProducts,
                                        })
                                    }
                                >
                                    {productOptions.map((el) => (
                                        <Option key={el.id} value={el.id}>
                                            {el.name}
                                        </Option>
                                    ))}
                                    {isFetchingProducts && (
                                        <Option key="loading" disabled>
                                            <S.LoadingOption>
                                                <Spin size="small" />
                                            </S.LoadingOption>
                                        </Option>
                                    )}
                                </S.SearchSelect>
                            </Form.Item>
                        </S.SearchWrapper>
                    </S.FormItemWrapper>
                    {productSelected && (
                        <ProductCard
                            barcode={productSelected.BarCodes?.[0]?.code || ''}
                            category={productSelected?.Category?.name || ''}
                            name={productSelected.name}
                            image_url={productSelected.image_url}
                        />
                    )}
                </Col>
            </Row>

            <S.FormsWrapper hasProduct={!productSelected}>
                <Row gutter={16}>
                    <Col span={24}>
                        <S.FormItemWrapper>
                            <S.FormItemLabel required>
                                Código de planograma
                            </S.FormItemLabel>
                            <S.FormItem
                                name={['planogram', 'planogram_code']}
                                rules={[
                                    {
                                        type: 'string',
                                        required: !!productSelected,
                                        message:
                                            'Digite o código do planograma',
                                    },
                                ]}
                            >
                                <Input
                                    placeholder="Digite o código do planograma"
                                    disabled={!productSelected}
                                />
                            </S.FormItem>
                        </S.FormItemWrapper>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <S.FormItemWrapper>
                            <S.FormItemLabel required>Status</S.FormItemLabel>
                            <S.FormItem
                                name={['planogram', 'status']}
                                rules={[
                                    {
                                        required: !!productSelected,
                                        message: 'Selecione o status',
                                        type: 'string',
                                    },
                                ]}
                            >
                                <Select
                                    placeholder="Selecione o status"
                                    options={statusOptions}
                                    allowClear
                                    disabled={!productSelected}
                                />
                            </S.FormItem>
                        </S.FormItemWrapper>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <S.FormItemWrapper>
                            <S.FormItemLabel required>
                                Quantidade padrão
                            </S.FormItemLabel>
                            <S.FormItem
                                name={['planogram', 'standard_amount']}
                                rules={[
                                    {
                                        required: !!productSelected,
                                        message: 'Informe a quantidade padrão',
                                        type: 'number',
                                    },
                                    {
                                        type: 'number',
                                        min: 1,
                                        message:
                                            'O valor mínimo de Quantidade padrão é 1',
                                    },
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    disabled={!productSelected}
                                />
                            </S.FormItem>
                        </S.FormItemWrapper>
                    </Col>
                    <Col xs={24} md={12}>
                        <S.FormItemWrapper>
                            <S.FormItemLabel required>
                                Nível de alerta
                            </S.FormItemLabel>
                            <S.FormItem
                                name={['planogram', 'alert_level']}
                                rules={[
                                    {
                                        required: !!productSelected,
                                        message: 'Informe o nível de alerta',
                                        type: 'number',
                                    },
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    disabled={!productSelected}
                                />
                            </S.FormItem>
                        </S.FormItemWrapper>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col xs={24} md={12}>
                        <S.FormItemWrapper>
                            <S.FormItemLabel required>
                                Preço Lista
                            </S.FormItemLabel>
                            <S.FormItem
                                name={['planogram', 'price_list_in_cents']}
                                rules={[
                                    {
                                        required: !!productSelected,
                                        message: 'Informe o preço lista',
                                    },
                                    {
                                        type: 'number',
                                        min: 1,
                                        message:
                                            'O preço deve ser maior que zero',
                                    },
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0,000"
                                    formatter={currencyFormatterToBRL}
                                    parser={parseBRLToCents}
                                    disabled={!productSelected}
                                />
                            </S.FormItem>
                        </S.FormItemWrapper>
                    </Col>
                    <Col xs={24} md={12}>
                        <S.FormItemWrapper>
                            <S.FormItemLabel required>
                                Preço App
                            </S.FormItemLabel>
                            <S.FormItem
                                name={['planogram', 'price_final_in_cents']}
                                rules={[
                                    {
                                        required: !!productSelected,
                                        message: 'Informe o preço lista',
                                    },
                                    {
                                        type: 'number',
                                        min: 1,
                                        message:
                                            'O preço deve ser maior que zero',
                                    },
                                ]}
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="0"
                                    formatter={currencyFormatterToBRL}
                                    parser={parseBRLToCents}
                                    disabled={!productSelected}
                                />
                            </S.FormItem>
                        </S.FormItemWrapper>
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={24}>
                        <S.FormItemWrapper>
                            <S.FormItemLabel required>
                                Quantidade em estoque
                            </S.FormItemLabel>
                            <S.FormItem
                                name={['planogram', 'stock_amount']}
                                rules={[
                                    {
                                        required: !!productSelected,
                                        message:
                                            'Informe a quantidade em estoque',
                                    },
                                ]}
                            >
                                <InputNumber
                                    placeholder="0"
                                    disabled={!productSelected}
                                    style={{ width: '100%' }}
                                />
                            </S.FormItem>
                        </S.FormItemWrapper>
                    </Col>
                </Row>
                <Form.Item name="searchText" hidden>
                    <Input />
                </Form.Item>
            </S.FormsWrapper>
        </S.Container>
    );
};
